import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowRight, Leaf, Shield, FlaskConical } from 'lucide-react';
import { productsApi } from '../api/client';
import { useT, useLanguage } from '../lib/i18n';
import ProductCard from '../components/ProductCard';

export default function Home() {
  const t = useT();
  const { locale } = useLanguage();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    productsApi.list({ featured: true }).then(r => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/f4e607f5-ded2-4ddd-93b1-775ccccff648/images/7e5db28448de9fea2ae11ffbde5277c836e0382ad3ca15a1a5f4bd1864297de8.png)` }} />
        <div className="absolute inset-0 bg-primary/85" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-8 opacity-70">
              <Leaf className="w-4 h-4 text-primary-foreground" />
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-primary-foreground">{t.hero.subtitle}</span>
            </div>
            <h1 className="font-serif text-6xl md:text-8xl font-light leading-[0.9] text-primary-foreground mb-8">
              {t.hero.title1}<br /><em className="font-normal">{t.hero.title2}</em>
            </h1>
            <p className="text-lg text-primary-foreground/80 mb-10 max-w-md leading-relaxed font-light">
              {t.hero.desc}
            </p>
            <Link
              to="/catalog"
              data-testid="hero-cta"
              className="inline-flex items-center gap-2.5 bg-accent text-accent-foreground px-8 py-3.5 rounded-full font-semibold hover:bg-accent/90 transition-all active:scale-95 shadow-lg"
            >
              {t.hero.cta}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="featured-section">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2 block">{t.catalog.featured}</span>
              <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground">{t.catalog.featured}</h2>
            </div>
            <Link to="/catalog" className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 font-medium">
              {t.nav.catalog} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {featured.map(p => <ProductCard key={p._id} product={p} />)}
          </div>
        </section>
      )}

      <section className="bg-secondary/50 border-y border-border py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
            {t.values.map((v, i) => {
              const icons = [Leaf, FlaskConical, Shield];
              const Icon = icons[i];
              return (
                <div key={v.title} className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-serif text-lg font-medium mb-1">{v.title}</h3>
                  <p className="text-sm text-muted-foreground">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
